/**
 * PanelWriter.ts — Cursor-tracking writer that positions all text within a PanelRegion.
 *
 * Replaces manual setCursor + write calls scattered across scenes.
 * Every write method positions the cursor at (currentRow, panel.startCol)
 * before writing, and advances the row counter afterward.
 * Writes silently no-op if currentRow exceeds panel.endRow (overflow protection).
 */

import type { Terminal } from './Terminal';
import type { TextEffects } from './TextEffects';
import type { PanelRegion } from './FrameRenderer';
import type { ColorName } from './ColorTheme';
import { wordWrap } from './wordWrap';

export class PanelWriter {
  private terminal: Terminal;
  private effects: TextEffects;
  private panel: PanelRegion;
  private currentRow: number;
  private promptReserved = false;

  // Text margins for better readability and word-wrap safety
  private readonly TEXT_MARGIN_LEFT = 2;
  private readonly TEXT_MARGIN_RIGHT = 3;

  constructor(terminal: Terminal, effects: TextEffects, panel: PanelRegion) {
    this.terminal = terminal;
    this.effects = effects;
    this.panel = panel;
    this.currentRow = panel.startRow;
  }

  /** Get maximum line width after applying text margins. */
  getMaxLineWidth(): number {
    return this.panel.width - this.TEXT_MARGIN_LEFT - this.TEXT_MARGIN_RIGHT;
  }

  /** Reserve the bottom row of the panel for the press-any-key prompt. */
  reservePromptRow(): void {
    this.promptReserved = true;
  }

  /** Effective last row for content (endRow or endRow-1 if prompt reserved). */
  private contentEndRow(): number {
    return this.promptReserved ? this.panel.endRow - 1 : this.panel.endRow;
  }

  // ─── Cursor ───────────────────────────────────────

  /** Current absolute row number. */
  getCurrentRow(): number {
    return this.currentRow;
  }

  /** How many rows remain before the panel's content end row. */
  getRemainingRows(): number {
    return Math.max(0, this.contentEndRow() - this.currentRow + 1);
  }

  /** True if at least `lines` rows remain in the panel. */
  hasRoom(lines = 1): boolean {
    return this.getRemainingRows() >= lines;
  }

  /** Reset the cursor to the panel's startRow, optionally offset by `rowOffset`. */
  reset(rowOffset = 0): void {
    this.currentRow = this.panel.startRow + rowOffset;
  }

  /** Skip `rows` rows (advance cursor without writing). */
  skip(rows = 1): void {
    this.currentRow += rows;
  }

  // ─── Writing ──────────────────────────────────────

  /** Write a line of text left-aligned with margins, word-wrapped if needed, then advance row. */
  writeLine(text: string, color?: ColorName): void {
    if (this.currentRow > this.contentEndRow()) return;
    const indent = ' '.repeat(this.TEXT_MARGIN_LEFT);
    const maxWidth = this.getMaxLineWidth();
    const wrapped = wordWrap(text, maxWidth);

    for (const line of wrapped) {
      if (this.currentRow > this.contentEndRow()) break;
      this.terminal.setCursor(this.currentRow, this.panel.startCol);
      this.terminal.clearLine(); // Clear stale content before writing
      this.terminal.write(indent + line, color);
      this.currentRow++;
    }
  }

  /** Write text centered within panel width, then advance row. */
  writeCentered(text: string, color?: ColorName): void {
    if (this.currentRow > this.contentEndRow()) return;
    const stripped = this.stripAnsi(text);
    const padding = Math.max(0, Math.floor((this.panel.width - stripped.length) / 2));
    this.terminal.setCursor(this.currentRow, this.panel.startCol);
    this.terminal.clearLine(); // Clear stale content before writing
    this.terminal.setCursor(this.currentRow, this.panel.startCol + padding);
    this.terminal.write(text, color);
    this.currentRow++;
  }

  /** Write pre-formatted ANSI text left-aligned, then advance row. */
  writeRaw(text: string): void {
    if (this.currentRow > this.contentEndRow()) return;
    this.terminal.setCursor(this.currentRow, this.panel.startCol);
    this.terminal.clearLine(); // Clear stale content before writing
    this.terminal.writeRaw(text);
    this.currentRow++;
  }

  /** Advance row without writing anything (no \r\n emission). */
  blankLine(): void {
    this.currentRow++;
  }

  // ─── Multi-Segment Lines ──────────────────────────

  /** Position cursor at (currentRow, startCol) for a multi-segment line. */
  beginLine(): void {
    if (this.currentRow > this.contentEndRow()) return;
    this.terminal.setCursor(this.currentRow, this.panel.startCol);
    this.terminal.clearLine(); // Clear stale content before writing
  }

  /** Append text at the current cursor position (no row advance). */
  write(text: string, color?: ColorName): void {
    if (this.currentRow > this.contentEndRow()) return;
    this.terminal.write(text, color);
  }

  /** Finish a multi-segment line by advancing the row. */
  endLine(): void {
    this.currentRow++;
  }

  // ─── Effects (awaitable, frame-aware) ─────────────

  /** Typewriter effect for a full line, positioned within the panel with margins. */
  async typewriterLine(text: string, color?: ColorName, speed?: number): Promise<void> {
    if (this.currentRow > this.contentEndRow()) return;
    const indent = ' '.repeat(this.TEXT_MARGIN_LEFT);
    const maxWidth = this.getMaxLineWidth();
    const wrapped = wordWrap(text, maxWidth);

    for (const line of wrapped) {
      if (this.currentRow > this.contentEndRow()) break;
      this.terminal.setCursor(this.currentRow, this.panel.startCol);
      this.terminal.clearLine(); // Clear stale content before writing
      await this.effects.typewriter(indent + line, color, speed);
      this.currentRow++;
    }
  }

  /** Typewriter with inline {color:text} markup, positioned within the panel with margins. */
  async typewriterRich(text: string, defaultColor?: ColorName, speed?: number): Promise<void> {
    const indent = ' '.repeat(this.TEXT_MARGIN_LEFT);
    const maxWidth = this.getMaxLineWidth();
    const wrapped = wordWrap(text, maxWidth);
    for (const wLine of wrapped) {
      if (this.currentRow > this.contentEndRow()) return;
      this.terminal.setCursor(this.currentRow, this.panel.startCol);
      await this.effects.typewriter(indent, 'white', 0); // Indent (instant)
      const segments = this.parseRichSegments(wLine, defaultColor ?? 'white');
      for (const seg of segments) {
        await this.effects.typewriter(seg.text, seg.color, speed);
      }
      this.currentRow++;
    }
  }

  /**
   * "Press any key" prompt, positioned at the reserved prompt row.
   * Adds a blank line separator before the prompt for better visual separation.
   * If `onResize` is provided, it is registered as a terminal resize handler
   * for the duration of the wait and cleaned up afterward. This lets scenes
   * redraw their content when the window is resized while waiting for input.
   */
  async pressAnyKey(prompt?: string, color?: ColorName, onResize?: () => void): Promise<void> {
    // Add blank line separator before prompt to prevent inline overlap
    this.blankLine();

    // Always place the prompt at the panel's endRow (the reserved row).
    // If we haven't overflowed, use currentRow if it's less than endRow.
    const row = Math.min(this.currentRow, this.panel.endRow);
    const indent = ' '.repeat(this.TEXT_MARGIN_LEFT);
    this.terminal.setCursor(row, this.panel.startCol);

    // Wire resize handler for the duration of the wait
    if (onResize) {
      this.terminal.onResize(onResize);
    }

    await this.effects.pressAnyKey(
      prompt ?? `${indent}Press any key to continue...`,
      color ?? 'khaki',
      this.panel.startCol,
    );

    // Clean up resize handler
    if (onResize) {
      this.terminal.offResize(onResize);
    }

    this.currentRow = row + 1;
  }

  /** Flash text at the current row within the panel. */
  async flash(text: string, color?: ColorName, count?: number, interval?: number): Promise<void> {
    if (this.currentRow > this.contentEndRow()) return;
    this.terminal.setCursor(this.currentRow, this.panel.startCol);
    await this.effects.flash(text, color, count, interval);
    this.currentRow++;
  }

  /** Skippable delay. */
  async delay(ms: number): Promise<void> {
    await this.effects.delay(ms);
  }

  /**
   * Typewriter a multi-line paragraph with paragraph-level skip.
   * Pressing a key during any line skips the rest of the paragraph instantly.
   * Each line is rendered with {color:text} markup via typewriterRich.
   */
  async typewriterParagraph(lines: string[], defaultColor?: ColorName, speed?: number): Promise<void> {
    this.effects.resetSkip();
    for (const line of lines) {
      if (line === '') {
        if (this.currentRow > this.contentEndRow()) break;
        this.currentRow++;
        continue;
      }
      // Dynamic wrap each paragraph line to max width with margins
      const indent = ' '.repeat(this.TEXT_MARGIN_LEFT);
      const maxWidth = this.getMaxLineWidth();
      const wrapped = wordWrap(line, maxWidth);
      for (const wLine of wrapped) {
        if (this.currentRow > this.contentEndRow()) break;
        this.terminal.setCursor(this.currentRow, this.panel.startCol);
        await this.effects.typewriter(indent, 'white', 0); // Indent (instant)
        const segments = this.parseRichSegments(wLine, defaultColor ?? 'white');
        for (const seg of segments) {
          await this.effects.typewriter(seg.text, seg.color, speed);
        }
        this.currentRow++;
      }
    }
    this.effects.resetSkip();
  }

  /**
   * Render a bordered hint box within panel bounds.
   * Used by HintManager for panel-aware tutorial hints.
   */
  async renderHintBox(title: string, lines: string[], color: ColorName, textSpeed = 15): Promise<void> {
    this.effects.resetSkip(); // Clear skip flag before hint rendering
    const boxWidth = Math.min(60, this.panel.width - 2);
    if (boxWidth < 20) return; // too narrow for a hint box

    // Top border with title
    if (this.currentRow > this.contentEndRow()) return;
    const titlePadded = ` ${title} `;
    const borderLeft = '╭' + '─'.repeat(2);
    const borderRight = '─'.repeat(Math.max(0, boxWidth - titlePadded.length - 4)) + '╮';
    this.terminal.setCursor(this.currentRow, this.panel.startCol + 1);
    this.terminal.write(borderLeft, color);
    this.terminal.write(titlePadded, color);
    this.terminal.write(borderRight, color);
    this.currentRow++;

    // Content lines
    for (const line of lines) {
      if (this.currentRow > this.contentEndRow()) break;
      this.terminal.setCursor(this.currentRow, this.panel.startCol + 1);
      this.terminal.write('│ ', color);
      const maxContent = boxWidth - 4;
      if (line === '') {
        this.terminal.write(' '.repeat(maxContent), color);
      } else {
        const displayText = line.length > maxContent ? line.slice(0, maxContent) : line;
        await this.effects.typewriter(displayText, 'white', textSpeed);
        const padding = Math.max(0, maxContent - displayText.length);
        this.terminal.write(' '.repeat(padding), color);
      }
      this.terminal.write(' │', color);
      this.currentRow++;
    }

    // Bottom border
    if (this.currentRow > this.contentEndRow()) return;
    this.terminal.setCursor(this.currentRow, this.panel.startCol + 1);
    this.terminal.write('╰' + '─'.repeat(boxWidth - 2) + '╯', color);
    this.currentRow++;
  }

  // ─── Menu Integration ─────────────────────────────

  /** Return positioning values for menu.show() at the current row. */
  menuConfig(): { startRow: number; startCol: number; maxWidth: number } {
    return {
      startRow: this.currentRow,
      startCol: this.panel.startCol,
      maxWidth: this.panel.width,
    };
  }

  // ─── Layout Helpers ───────────────────────────────

  /**
   * Calculate a vertical offset to center `contentLines` rows within the panel.
   * Returns 0 if content fills more than half the panel (no centering needed).
   */
  calculateVerticalOffset(contentLines: number): number {
    const available = this.contentEndRow() - this.panel.startRow + 1;
    if (contentLines >= available * 0.5) return 0;
    return Math.max(0, Math.floor((available - contentLines) / 2));
  }

  /**
   * Set up the writer for a vertically-centered narrative layout.
   * Call this before writing content to auto-center a block of `contentLines` rows.
   * Also reserves the prompt row automatically.
   *
   * @param contentLines - Estimated number of content lines to render
   * @returns The row offset applied (0 if no centering)
   */
  narrativeLayout(contentLines: number): number {
    this.reservePromptRow();
    const offset = this.calculateVerticalOffset(contentLines);
    if (offset > 0) {
      this.reset(offset);
    }
    return offset;
  }

  /**
   * Auto-center: skip rows to vertically center the given content height.
   * Only applies offset if fill < 50%.
   */
  autoCenterIfSparse(contentLines: number): void {
    const available = this.getRemainingRows();
    if (contentLines < available * 0.5) {
      const offset = Math.floor((available - contentLines) / 2);
      if (offset > 1) {
        this.skip(offset);
      }
    }
  }

  // ─── Helpers ──────────────────────────────────────

  private stripAnsi(text: string): string {
    // eslint-disable-next-line no-control-regex
    return text.replace(/\x1b\[[0-9;]*m/g, '');
  }

  /** Parse `{color:text}` markup into segments (same logic as TextEffects.typewriterRich). */
  private parseRichSegments(text: string, defaultColor: ColorName): Array<{ color: ColorName; text: string }> {
    const segments: Array<{ color: ColorName; text: string }> = [];
    let remaining = text;

    while (remaining.length > 0) {
      const braceIdx = remaining.indexOf('{');
      if (braceIdx === -1) {
        segments.push({ color: defaultColor, text: remaining });
        break;
      }

      if (braceIdx > 0) {
        segments.push({ color: defaultColor, text: remaining.slice(0, braceIdx) });
      }

      const closeIdx = remaining.indexOf('}', braceIdx);
      if (closeIdx === -1) {
        segments.push({ color: defaultColor, text: remaining.slice(braceIdx) });
        break;
      }

      const inner = remaining.slice(braceIdx + 1, closeIdx);
      const colonIdx = inner.indexOf(':');
      if (colonIdx === -1) {
        segments.push({ color: defaultColor, text: remaining.slice(braceIdx, closeIdx + 1) });
      } else {
        const colorName = inner.slice(0, colonIdx) as ColorName;
        const colorText = inner.slice(colonIdx + 1);
        segments.push({ color: colorName, text: colorText });
      }

      remaining = remaining.slice(closeIdx + 1);
    }

    return segments;
  }
}
